/**
 * The station alerts' settings, in Settings, Email (the station workspaces plan, step 7c; GET and PUT
 * /v1/twin/settings/alerts): how long an open alert waits for an acknowledgement before it steps up, with whom it then
 * reaches in words, and whether the platform emails alerts at all (read-only: the platform's own switch). An org admin
 * edits the minutes; every other member reads them. Shown only where the settings can be read (the live platform).
 *
 * StationAlertSettingsView is pure (drawn in tests without providers); StationAlertSettings holds the read, the draft
 * and the save. Every word is lib/plant/station-alerts'.
 */
import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import {
  ALERT_SETTINGS_MAX,
  ALERT_SETTINGS_MIN,
  stationAlertsHidden,
  useAlertSettingsQuery,
  useSaveAlertSettings,
  type AlertSettings,
} from "@/lib/api/station-alerts";
import {
  alertEmailText,
  alertSettingsErrorWords,
  escalationProblem,
  escalationTiersText,
} from "@/lib/plant/station-alerts";
import { useOrg } from "@/lib/org-context";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";

export interface StationAlertSettingsViewProps {
  state: "loading" | "failed" | "ready";
  settings?: AlertSettings;
  /** org.admin: the minutes are editable; for anyone else they are read. */
  canEdit: boolean;
  /** The minutes as typed (the saved ones until edited). */
  draft: string;
  saving?: boolean;
  saved?: boolean;
  error?: string | null;
  onDraft?: (v: string) => void;
  onSave?: () => void;
}

const TIERS_ID = "station-alert-escalation-tiers";

export function StationAlertSettingsView(p: StationAlertSettingsViewProps) {
  if (p.state === "loading") {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-on-surface-muted">
        <Loader2 aria-hidden className="size-4 animate-spin" /> Reading the station alerts&apos; settings
      </p>
    );
  }
  if (p.state === "failed" || !p.settings) {
    return (
      <p role="alert" className="text-sm text-danger">
        The station alerts&apos; settings could not be read. Reload the page to try again.
      </p>
    );
  }
  const s = p.settings;
  const problem = escalationProblem(p.draft);
  const changed = p.draft.trim() !== String(s.escalation_minutes);
  // The tiers in words follow the minutes typed, while they read.
  const minutes = problem ? s.escalation_minutes : Number(p.draft.trim());
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-on-surface">{alertEmailText(s.email_enabled)}</p>
      {p.canEdit ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            p.onSave?.();
          }}
        >
          <div className="flex flex-col gap-1">
            <label htmlFor="station-alert-escalation" className="text-xs text-on-surface-muted">
              Minutes an alert waits for an acknowledgement ({ALERT_SETTINGS_MIN} to {ALERT_SETTINGS_MAX})
            </label>
            <Input
              id="station-alert-escalation"
              type="number"
              inputMode="numeric"
              min={ALERT_SETTINGS_MIN}
              max={ALERT_SETTINGS_MAX}
              step={1}
              value={p.draft}
              onChange={(e) => p.onDraft?.(e.target.value)}
              aria-invalid={changed && problem ? true : undefined}
              aria-describedby={TIERS_ID}
              className="w-28"
            />
          </div>
          <Button type="submit" size="sm" disabled={!changed || Boolean(problem) || Boolean(p.saving)}>
            {p.saving ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <Save aria-hidden className="size-4" />}
            Save
          </Button>
        </form>
      ) : null}
      <p id={TIERS_ID} className="text-xs text-on-surface-muted">
        {escalationTiersText(minutes)}
        {!p.canEdit && " An org admin changes the minutes."}
      </p>
      {p.canEdit && changed && problem && (
        <p role="alert" className="text-xs text-danger">
          {problem}
        </p>
      )}
      {p.error && (
        <p role="alert" className="text-xs text-danger">
          {p.error}
        </p>
      )}
      {p.saved && !changed && !p.error && (
        <p role="status" className="text-xs text-on-surface-muted">
          Saved.
        </p>
      )}
    </div>
  );
}

/** The org's station alert settings: read, drafted and saved. Hidden where they cannot be read. */
export function StationAlertSettings() {
  const { canAdmin } = useOrg();
  const settings = useAlertSettingsQuery();
  const save = useSaveAlertSettings();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Not read here at all (not the live platform), or refused for good: no section to show.
  if ((settings.isPending && settings.fetchStatus === "idle") || stationAlertsHidden(settings.error)) return null;
  const value = draft ?? (settings.data ? String(settings.data.escalation_minutes) : "");
  return (
    <StationAlertSettingsView
      state={settings.data ? "ready" : settings.isError ? "failed" : "loading"}
      settings={settings.data}
      canEdit={canAdmin}
      draft={value}
      saving={save.isPending}
      saved={save.isSuccess}
      error={error}
      onDraft={(v) => {
        setError(null);
        setDraft(v);
      }}
      onSave={() => {
        if (escalationProblem(value)) return;
        setError(null);
        save.mutate(Number(value.trim()), {
          onSuccess: () => setDraft(null),
          onError: (err) => setError(alertSettingsErrorWords(err)),
        });
      }}
    />
  );
}
